/**
 * Shared layout primitive for static company / legal pages.
 *
 * Provides:
 *  - Top "ribbon" navigation back to the marketing site
 *  - Hero header with eyebrow + title + last-updated date
 *  - Article container constrained to a readable measure
 *  - Section helpers
 *  - Responsive "tables" rendered as stacked cards on mobile (no overflow)
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { EuStars } from "./EuStars";
import SiteFooter from "./SiteFooter";
import { useLang } from "../lib/i18n";

export function LegalShell({ eyebrow, title, subtitle, lastUpdated, children }) {
    const { lang } = useLang();
    return (
        <div className="min-h-screen flex flex-col bg-[#F5F0E8]">
            <header className="bg-[#0D1B3E] text-[#F5F0E8]">
                <div className="container-edit py-5 flex items-center justify-between gap-4">
                    <Link to="/" className="flex items-center gap-3" data-testid="legal-back-home">
                        <EuStars size={28} />
                        <div className="leading-tight">
                            <div className="font-display text-lg tracking-tight">EuroLaw Hub</div>
                            <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                {eyebrow}
                            </div>
                        </div>
                    </Link>
                    <Link
                        to="/"
                        className="text-xs uppercase tracking-[0.18em] text-[#F5F0E8]/70 hover:text-[#C9A84C] flex items-center gap-1.5"
                    >
                        <ArrowLeft size={13} /> Home
                    </Link>
                </div>
            </header>

            {lang === "tr" && (
                <div className="bg-[#EFE8DB] border-b border-[#0D1B3E]/10 text-sm text-[#0D1B3E]/80 px-4 py-2.5 text-center" data-testid="legal-tr-note">
                    Bu sayfa şu an yalnızca İngilizce sunulmaktadır; bağlayıcı metin İngilizce sürümdür. Hukuki bilgidir, hukuki tavsiye değildir.
                </div>
            )}
            <main className="flex-1">
                <div className="container-edit py-10 lg:py-16">
                    <div className="max-w-3xl">
                        <h1 className="font-display text-4xl lg:text-5xl tracking-tight">
                            {title}
                        </h1>
                        {subtitle && (
                            <p className="mt-4 text-[#0D1B3E]/80 leading-relaxed">
                                {subtitle}
                            </p>
                        )}
                        {lastUpdated && (
                            <p className="mt-5 text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/55">
                                Last updated · {lastUpdated}
                            </p>
                        )}
                    </div>

                    <article className="prose-eurolaw max-w-3xl mt-12">
                        {children}
                    </article>
                </div>
            </main>

            <SiteFooter />
        </div>
    );
}

export function Section({ id, title, children }) {
    return (
        <section id={id} className="mb-12" aria-labelledby={id ? `${id}-h` : undefined}>
            {title && (
                <h2
                    id={id ? `${id}-h` : undefined}
                    className="font-display text-2xl lg:text-3xl tracking-tight mt-2"
                >
                    {title}
                </h2>
            )}
            <div className="mt-4 text-[#0D1B3E]/90 leading-relaxed space-y-4 text-[15px]">
                {children}
            </div>
        </section>
    );
}

export function ValuePillars({ items }) {
    return (
        <div className="grid gap-4 sm:grid-cols-3 my-7">
            {items.map((it) => (
                <div key={it.title} className="card-flat p-5">
                    <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                        {it.label}
                    </div>
                    <div className="mt-1 font-display text-xl tracking-tight">
                        {it.title}
                    </div>
                    <p className="mt-2 text-sm text-[#0D1B3E]/80 leading-relaxed">
                        {it.body}
                    </p>
                </div>
            ))}
        </div>
    );
}

/**
 * Responsive data table rendered as stacked cards on mobile (no overflow).
 * Pass: { columns: [{key, label}], rows: [object keyed by column.key] }
 */
export function ResponsiveTable({ columns, rows, testId }) {
    return (
        <>
            {/* Desktop / tablet: real table */}
            <div className="hidden md:block overflow-hidden border border-[#0D1B3E]/10" data-testid={testId}>
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-[#0D1B3E]/5">
                            {columns.map((c) => (
                                <th
                                    key={c.key}
                                    className="text-left px-4 py-3 label-eyebrow whitespace-nowrap"
                                >
                                    {c.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, i) => (
                            <tr
                                key={i}
                                className="border-t border-[#0D1B3E]/10 align-top"
                            >
                                {columns.map((c) => (
                                    <td
                                        key={c.key}
                                        className="px-4 py-3 text-[#0D1B3E]/85"
                                    >
                                        {row[c.key]}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile: stacked cards */}
            <div className="md:hidden space-y-3" data-testid={testId ? `${testId}-mobile` : undefined}>
                {rows.map((row, i) => (
                    <div
                        key={i}
                        className="card-flat p-4 text-sm text-[#0D1B3E]/85"
                    >
                        {columns.map((c, j) => (
                            <div key={c.key} className={j === 0 ? "" : "mt-3"}>
                                <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                                    {c.label}
                                </div>
                                <div className="mt-1 leading-relaxed">
                                    {row[c.key]}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </>
    );
}
